import React from "react";
import { Sparkles, Mountain, Compass, Flame, Telescope, Camera, Music, Heart } from "lucide-react";

/**
 * Signature Experiences — the "what makes this retreat actually special"
 * section, used by both SolsticeRetreat and SaddleAndSoul pages.
 *
 * Props:
 *   - variant: "solstice" | "saddle"   (chooses hat material, stargazing inclusion)
 *   - className?: extra wrapper classes
 */
export default function SignatureExperiences({ variant = "solstice", className = "" }) {
  const isSaddle = variant === "saddle";
  const hatMaterial = isSaddle ? "felt + wool" : "straw + palm";

  const experiences = [
    {
      icon: Mountain,
      eyebrow: "Mineral waters",
      title: "Mount Princeton Hot Springs",
      body: (
        <>
          One of Colorado's most magical mountain retreats. Soak in <strong>natural in-creek hot springs</strong> where mineral-rich waters meet the cold mountain creek — or rest in the spring-fed pools beneath open Rocky Mountain sky.
          <a href="https://mtprinceton.com/" target="_blank" rel="noopener noreferrer" className="inline-block ml-1 underline text-aviana-plum hover:text-aviana-goldDeep">visit&nbsp;mtprinceton.com&nbsp;↗</a>
        </>
      ),
      tag: "Half-day excursion",
      image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/qoix2821_MtPHotSprings.jpg",
    },
    {
      icon: Sparkles,
      eyebrow: "Custom hat bar",
      title: "Craft your one-of-a-kind hat",
      body: (
        <>
          Sit at the curated hat bar and create your own <strong>{hatMaterial}</strong> hat — leather bands, vintage ribbons, real feathers, dried botanicals, charms. No two leave the same. <em>This is the souvenir you'll wear for years.</em>
        </>
      ),
      tag: "2-hour workshop",
      image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/sdaqzk38_503504502_17995734671800166_7491024924212762111_n.jpg",
    },
    {
      icon: Flame,
      eyebrow: "Witcher Mountain Apothecary",
      title: "Cold-process soap making",
      body: (
        <>
          A small-batch <strong>cold-process</strong> soap workshop with local apothecary <strong>Witcher Mountain</strong> — lye prepared in advance, safety gear provided, all the pretty without the prep work. You'll leave with approximately <strong>10 bars of your own soap</strong> (or split your loaf with a sister and take five each).
        </>
      ),
      tag: "≈ 2 hours · sets 24h before unmolding",
      image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/lavivnn0_2025-02-08%20%281%29.webp",
    },
    {
      icon: Compass,
      eyebrow: "Equine connection",
      title: "Trail rides + sacred herd time",
      body: (
        <>
          Two guided two-hour trail rides through mountain terrain, plus quiet ground time with the herd — observing, listening, and letting the horses mirror back what's ready to be seen. <em>No prior experience required.</em>
        </>
      ),
      tag: "Beginner-friendly",
      image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/ptri7573_1A986BA1-7FFA-493D-AA85-6C6A8E1B6830_1_105_c.jpeg",
    },
    {
      icon: Music,
      eyebrow: "Inner alchemy",
      title: "Drumming, painting, ceremony",
      body: (
        <>
          A shamanic drumming journey followed by intuitive painting — let the rhythm move what words cannot, then let color and brush do the rest. Soft fires, sacred music, and full-moon meditations woven through every evening.
        </>
      ),
      tag: "Evening ritual",
      image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/r6r39c77_C48030CF-4130-4FEE-8B8A-3C6DDA5294B5_1_105_c.jpeg",
    },
    ...(isSaddle
      ? [{
          icon: Telescope,
          eyebrow: "Saddle & Soul exclusive",
          title: "Stargazing with a pro telescope",
          body: (
            <>
              On equinox evenings the Rocky Mountain sky becomes a cathedral. We'll bring out a <strong>professional-grade telescope</strong> — Saturn's rings, Jupiter's moons, distant nebulae, and the whisper of the Milky Way overhead.
            </>
          ),
          tag: "September only · weather permitting",
          image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/pmvy0s2h_download.webp",
        }]
      : [{
          icon: Camera,
          eyebrow: "Captured + held",
          title: "Memorable photography included",
          body: (
            <>
              Candid moments throughout the week — at the springs, on horseback, around the fire — captured and gifted back to you. <em>Come home with photographs of yourself laughing freely again.</em>
            </>
          ),
          tag: "Delivered after the retreat",
          image: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/18opiwi2_IMG_0587.jpeg",
        }]),
  ];

  return (
    <section className={`py-24 md:py-28 bg-aviana-cream/40 ${className}`}>
      <div className="container-aviana max-w-6xl">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <p className="overline-plum mb-3 inline-flex items-center gap-2">
            <Heart size={14} className="text-aviana-gold" /> The details that hold you
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-aviana-ink leading-[1.04]">
            Designed so <em className="italic gold-text">you can simply receive.</em>
          </h2>
          <p className="mt-6 text-aviana-inkSoft leading-[1.85] font-serif italic text-[18px] md:text-[19px]">
            Every detail is held in advance — logistics, meals, instructors, supplies, surprise moments —
            so the only thing left for you to do is exhale, play, and let yourself be moved.
          </p>
        </div>

        {/* Experience cards */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {experiences.map((e, idx) => {
            const Icon = e.icon;
            return (
              <div
                key={e.title}
                className="group relative bg-aviana-cream rounded-[1.5rem] border border-aviana-border p-7 md:p-8 hover:border-aviana-gold/60 transition-all duration-500 hover:shadow-[0_12px_36px_-16px_rgba(126,63,143,0.22)] overflow-hidden"
                data-testid={`signature-exp-${idx}`}
              >
                {/* Optional image (hat / soap photos) */}
                {e.image && (
                  <div className="relative mb-5 -mx-7 -mt-7 md:-mx-8 md:-mt-8 aspect-[16/9] overflow-hidden border-b border-aviana-border">
                    <img src={e.image} alt={e.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]" />
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-aviana-gold/15 border border-aviana-gold/40 flex items-center justify-center">
                    <Icon size={20} className="text-aviana-goldDeep" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="overline-plum mb-1.5 text-[10px]">{e.eyebrow}</p>
                    <h3 className="font-display text-[22px] md:text-[24px] text-aviana-ink leading-tight mb-3 group-hover:text-aviana-plum transition-colors">
                      {e.title}
                    </h3>
                    <p className="text-aviana-inkSoft text-[14.5px] leading-[1.65]">
                      {e.body}
                    </p>
                    {e.tag && (
                      <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-aviana-goldDeep">
                        {e.tag}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Closing whisper */}
        <p className="text-center mt-14 max-w-2xl mx-auto font-serif italic text-aviana-muted text-[17px] leading-[1.7]">
          A retreat is the sum of a thousand small choices made in advance — so you never have to
          make a single one. <span className="text-aviana-goldDeep not-italic">This is yours, fully held.</span>
        </p>
      </div>
    </section>
  );
}
